import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Copy, Calendar, Percent, DollarSign, Users, Eye, Printer, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { VoucherService, Voucher } from '@/services/voucherService';
import { useAuthState } from '@/hooks/useAuthState';

interface VoucherManagerProps {
  partnerId: number;
  partnerName: string;
}

const VoucherManager = ({ partnerId, partnerName }: VoucherManagerProps) => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [deleteVoucherId, setDeleteVoucherId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [printStats, setPrintStats] = useState<Record<number, number>>({});
  const { toast } = useToast();
  const { user } = useAuthState();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discount_type: 'percentage' as 'percentage' | 'fixed',
    discount_value: 0,
    start_date: '',
    end_date: '',
    is_active: true,
    usage_limit: null as number | null
  });

  useEffect(() => {
    loadVouchers();
    loadPrintStats();
    
    // Atualizar estatísticas a cada 10 segundos
    const statsInterval = setInterval(() => {
      loadPrintStats();
    }, 10000);
    
    // Atualizar estatísticas quando a janela recebe foco
    const handleFocus = () => {
      loadPrintStats();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(statsInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [partnerId]);

  const loadPrintStats = async () => {
    try {
      const stats = await VoucherService.getPrintStats();
      const statsMap: Record<number, number> = {};
      stats.forEach((stat: any) => {
        statsMap[stat.id] = stat.print_count || 0;
      });
      setPrintStats(statsMap);
    } catch (error) {
      console.error('Erro ao carregar estatísticas de impressões:', error);
      // Não mostrar erro ao usuário, apenas logar
    }
  };

  const loadVouchers = async () => {
    try {
      setLoading(true);
      const vouchersData = await VoucherService.getVouchersByPartner(partnerId);
      
      // Filtrar apenas vouchers não excluídos (deleted_at IS NULL)
      const activeVouchers = vouchersData.filter(voucher => !voucher.deleted_at);
      
      setVouchers(activeVouchers);
    } catch (error) {
      console.error('Erro ao carregar vouchers:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os vouchers",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      title: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      start_date: '',
      end_date: '',
      is_active: true,
      usage_limit: null
    });
    setIsCreateModalOpen(true);
  };

  const handleEdit = (voucher: Voucher) => {
    // Converter datas do formato do banco (YYYY-MM-DD HH:mm:ss) para formato do input (YYYY-MM-DD)
    const formatDateForInput = (dateString: string | null | undefined) => {
      if (!dateString) {
        return '';
      }
      // Se já está no formato YYYY-MM-DD, retornar diretamente
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      // Se tem espaço, pegar apenas a parte da data, ignorando o horário
      return dateString.split(' ')[0];
    };

    // Função para formatar data para exibição sem problemas de timezone
    const formatDateForDisplay = (dateString: string | null | undefined) => {
      if (!dateString) {
        return 'Não informado';
      }
      // Extrair apenas a parte da data (YYYY-MM-DD)
      const dateOnly = dateString.split(' ')[0].split('T')[0];
      const [year, month, day] = dateOnly.split('-');
      // Criar Date usando componentes locais para evitar timezone
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('pt-BR');
    };

    setFormData({
      title: voucher.title,
      description: voucher.description,
      discount_type: voucher.discount_type,
      discount_value: voucher.discount_value,
      start_date: formatDateForInput(voucher.start_date),
      end_date: formatDateForInput(voucher.end_date),
      is_active: voucher.is_active,
      usage_limit: voucher.usage_limit
    });
    setSelectedVoucher(voucher);
    setIsEditModalOpen(true);
  };

  const handleView = (voucher: Voucher) => {
    setSelectedVoucher(voucher);
    setIsViewModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const userId = user?.id || null;
      
      if (isCreateModalOpen) {
        // Garantir que datas vazias sejam null
        // O input type="date" retorna YYYY-MM-DD, usar diretamente sem conversão
        const startDate = formData.start_date && formData.start_date.trim() !== '' ? formData.start_date.trim() : null;
        const endDate = formData.end_date && formData.end_date.trim() !== '' ? formData.end_date.trim() : null;
        
        const voucherData = {
          partner_id: partnerId,
          ...formData,
          start_date: startDate, // Já está no formato YYYY-MM-DD do input
          end_date: endDate      // Já está no formato YYYY-MM-DD do input
        };
        
        console.log('📅 Datas antes de enviar:', { start_date: startDate, end_date: endDate });
        await VoucherService.createVoucher(voucherData, userId);
        toast({
          title: "Sucesso",
          description: "Voucher criado com sucesso!",
        });
      } else if (isEditModalOpen && selectedVoucher) {
        // Garantir que datas vazias sejam null
        // O input type="date" retorna YYYY-MM-DD, usar diretamente sem conversão
        const startDate = formData.start_date && formData.start_date.trim() !== '' ? formData.start_date.trim() : null;
        const endDate = formData.end_date && formData.end_date.trim() !== '' ? formData.end_date.trim() : null;
        
        const updateData = {
          ...formData,
          start_date: startDate, // Já está no formato YYYY-MM-DD do input
          end_date: endDate      // Já está no formato YYYY-MM-DD do input
        };
        
        console.log('📅 Datas antes de atualizar:', { start_date: startDate, end_date: endDate });
        await VoucherService.updateVoucher(selectedVoucher.id, updateData, userId);
        toast({
          title: "Sucesso",
          description: "Voucher atualizado com sucesso!",
        });
      }
      
      await loadVouchers();
      handleClose();
    } catch (error) {
      console.error('Erro ao salvar voucher:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o voucher",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteVoucherId) return;
    
    try {
      const userId = user?.id || null;
      await VoucherService.softDeleteVoucher(deleteVoucherId, userId);
      toast({
        title: "Sucesso",
        description: "Voucher excluído com sucesso!",
      });
      await loadVouchers();
    } catch (error) {
      console.error('Erro ao excluir voucher:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o voucher",
        variant: "destructive",
      });
    } finally {
      setDeleteVoucherId(null);
    }
  };

  const handleClose = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsViewModalOpen(false);
    setSelectedVoucher(null);
    setFormData({
      title: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      start_date: '',
      end_date: '',
      is_active: true,
      usage_limit: null
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copiado!",
      description: "Código copiado para a área de transferência",
    });
  };

  const formatDiscount = (voucher: Voucher) => {
    if (voucher.discount_type === 'percentage') {
      return `${voucher.discount_value}%`;
    } else {
      return `R$ ${voucher.discount_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    }
  };

  const isVoucherActive = (voucher: Voucher) => {
    const today = new Date();
    const startDate = new Date(voucher.start_date);
    const endDate = new Date(voucher.end_date);
    
    return voucher.is_active && 
           today >= startDate && 
           today <= endDate &&
           (voucher.usage_limit === null || voucher.usage_count < voucher.usage_limit);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vouchers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Vouchers</CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                onClick={loadPrintStats} 
                size="sm" 
                variant="outline"
                title="Atualizar estatísticas de impressões"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button onClick={handleCreate} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Novo Voucher
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {vouchers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum voucher criado ainda</p>
              <p className="text-sm">Clique em "Novo Voucher" para criar o primeiro</p>
            </div>
          ) : (
            <div className="space-y-4">
              {vouchers.map((voucher) => (
                <div key={voucher.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{voucher.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          isVoucherActive(voucher) 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {isVoucherActive(voucher) ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                        <span className="flex items-center gap-1">
                          {voucher.discount_type === 'percentage' ? (
                            <Percent className="h-4 w-4" />
                          ) : (
                            <DollarSign className="h-4 w-4" />
                          )}
                          {formatDiscount(voucher)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span className="font-medium">
                            {(() => {
                              const formatDate = (dateString: string | null | undefined) => {
                                if (!dateString) return 'Não informado';
                                const dateOnly = dateString.split(' ')[0].split('T')[0];
                                const [year, month, day] = dateOnly.split('-');
                                const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                return date.toLocaleDateString('pt-BR');
                              };
                              return `${formatDate(voucher.start_date)} até ${formatDate(voucher.end_date)}`;
                            })()}
                          </span>
                        </span>
                        {voucher.usage_limit && (
                          <span>
                            {voucher.usage_count}/{voucher.usage_limit} usos
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-primary font-medium">
                          <Printer className="h-4 w-4" />
                          {printStats[voucher.id] || 0} impressões
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                          {voucher.code}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(voucher.code)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleView(voucher)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(voucher)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteVoucherId(voucher.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Criação/Edição */}
      <Dialog open={isCreateModalOpen || isEditModalOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {isCreateModalOpen ? 'Criar Voucher' : 'Editar Voucher'}
            </DialogTitle>
            <DialogDescription>
              {isCreateModalOpen 
                ? 'Crie um novo voucher para seus clientes' 
                : 'Edite as informações do voucher'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Título</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ex: Desconto Especial"
                />
              </div>
              <div>
                <Label htmlFor="discount_type">Tipo de Desconto</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(value: 'percentage' | 'fixed') => 
                    setFormData(prev => ({ ...prev, discount_type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentual (%)</SelectItem>
                    <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="discount_value">
                {formData.discount_type === 'percentage' ? 'Percentual de Desconto' : 'Valor do Desconto (R$)'}
              </Label>
              <Input
                id="discount_value"
                type="number"
                value={formData.discount_value}
                onChange={(e) => setFormData(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                placeholder={formData.discount_type === 'percentage' ? '15' : '20.00'}
                min="0"
                step={formData.discount_type === 'percentage' ? '1' : '0.01'}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date" className="text-sm font-medium">
                  Data de Início
                </Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="mt-1"
                />
                {formData.start_date && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {(() => {
                      // Parse da data sem timezone (YYYY-MM-DD)
                      const [year, month, day] = formData.start_date.split('-');
                      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                      return date.toLocaleDateString('pt-BR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      });
                    })()}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="end_date" className="text-sm font-medium">
                  Data de Fim
                </Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="mt-1"
                />
                {formData.end_date && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {(() => {
                      // Parse da data sem timezone (YYYY-MM-DD)
                      const [year, month, day] = formData.end_date.split('-');
                      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                      return date.toLocaleDateString('pt-BR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      });
                    })()}
                  </p>
                )}
              </div>
            </div>
            
            <div>
              <Label htmlFor="usage_limit">Limite de Uso (opcional)</Label>
              <Input
                id="usage_limit"
                type="number"
                value={formData.usage_limit || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  usage_limit: e.target.value ? parseInt(e.target.value) : null 
                }))}
                placeholder="Deixe vazio para uso ilimitado"
                min="1"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Descrição e Regras</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descreva as regras e condições do voucher..."
                rows={4}
                maxLength={10000}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.description.length}/10000 caracteres
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is_active">Voucher Ativo</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : (isCreateModalOpen ? 'Criar' : 'Salvar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Visualização */}
      <Dialog open={isViewModalOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Detalhes do Voucher</DialogTitle>
          </DialogHeader>
          
          {selectedVoucher && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{selectedVoucher.title}</h3>
                <p className="text-sm text-muted-foreground">Código: {selectedVoucher.code}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium">Desconto</p>
                  <p>{formatDiscount(selectedVoucher)}</p>
                </div>
                <div>
                  <p className="font-medium">Período</p>
                  <p>
                    {(() => {
                      const formatDate = (dateString: string | null | undefined) => {
                        if (!dateString) return 'Não informado';
                        const dateOnly = dateString.split(' ')[0].split('T')[0];
                        const [year, month, day] = dateOnly.split('-');
                        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                        return date.toLocaleDateString('pt-BR');
                      };
                      return `${formatDate(selectedVoucher.start_date)} - ${formatDate(selectedVoucher.end_date)}`;
                    })()}
                  </p>
                </div>
                <div>
                  <p className="font-medium">Status</p>
                  <p className={isVoucherActive(selectedVoucher) ? 'text-green-600' : 'text-red-600'}>
                    {isVoucherActive(selectedVoucher) ? 'Ativo' : 'Inativo'}
                  </p>
                </div>
                <div>
                  <p className="font-medium">Uso</p>
                  <p>
                    {selectedVoucher.usage_count}
                    {selectedVoucher.usage_limit ? `/${selectedVoucher.usage_limit}` : ' (ilimitado)'}
                  </p>
                </div>
              </div>
              
              {selectedVoucher.description && (
                <div>
                  <p className="font-medium mb-2">Descrição e Regras</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {selectedVoucher.description}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <AlertDialog open={!!deleteVoucherId} onOpenChange={() => setDeleteVoucherId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este voucher? Esta ação não pode ser desfeita e o voucher será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default VoucherManager;
